import { useState } from "react";
import { useTranslation } from "react-i18next";

import { can, useMe } from "@/auth/useMe";
import { Dialog } from "@/components/Dialog";
import {
  Button,
  Card,
  EmptyState,
  ErrorState,
  Page,
  Spinner,
  Table,
  Tag,
} from "@/components/ui";
import type { ManagedTemplate } from "@/plantillas/model";
import { PlantillaForm } from "@/plantillas/PlantillaForm";
import {
  useDeleteTemplate,
  useManagedTemplates,
  useSyncTemplates,
} from "@/plantillas/queries";

/** The permission the API enforces; granted to Admin and Sales Manager. */
const MANAGE = "whatsapp.manage_templates";

/**
 * Plantillas de WhatsApp — where management writes the messages a seller may
 * open a conversation with. A template is the only first message Meta accepts,
 * so this screen decides what the business can say to a client it has not
 * heard from in 24 hours.
 *
 * Gated on the permission rather than a role: `is_manager` and `can_triage`
 * answer other questions, and the API checks exactly this string.
 */
export function Plantillas() {
  const { t } = useTranslation();
  const { data: me, isPending } = useMe();

  if (isPending) return <Spinner label={t("common.loading")} />;
  if (!can(me, MANAGE)) {
    return (
      <Page title={t("templates.title")}>
        <EmptyState>{t("templates.notAllowed")}</EmptyState>
      </Page>
    );
  }
  return <TemplatesView />;
}

function TemplatesView() {
  const { t } = useTranslation();
  const templates = useManagedTemplates();
  const sync = useSyncTemplates();
  // `{}` is a new template; `{ template }` is editing that one.
  const [editing, setEditing] = useState<{ template?: ManagedTemplate } | null>(null);
  const [deleting, setDeleting] = useState<ManagedTemplate | null>(null);
  const rows = templates.data?.results ?? [];

  const language = (template: ManagedTemplate) =>
    t(`templates.languages.${template.language}`, { defaultValue: template.language });

  return (
    <Page
      title={t("templates.title")}
      subtitle={t("templates.subtitle")}
      actions={
        <>
          <Button disabled={sync.isPending} onClick={() => sync.mutate()}>
            {sync.isPending ? t("templates.syncing") : t("templates.sync")}
          </Button>
          <Button variant="primary" onClick={() => setEditing({})}>
            {t("templates.new")}
          </Button>
        </>
      }
    >
      {sync.isSuccess ? (
        <p className="mb-4 text-sm text-mute-600" aria-live="polite">
          {t("templates.synced", { count: sync.data.synced })}
        </p>
      ) : null}
      {sync.isError ? (
        <p role="alert" className="mb-4 text-sm text-brand-700">
          {sync.error.message}
        </p>
      ) : null}

      <Card>
        {templates.isPending ? (
          <Spinner label={t("templates.loading")} />
        ) : templates.isError ? (
          <ErrorState
            message={t("templates.loadFailed")}
            onRetry={() => void templates.refetch()}
          />
        ) : rows.length === 0 ? (
          <EmptyState>{t("templates.empty")}</EmptyState>
        ) : (
          <>
            {/* A table from `sm` up and cards below it, as on Prospectos. The
                table is wider than a phone, and on a phone a page wider than
                the screen stretches the whole layout — every tap then lands
                beside what it aimed at. */}
            <div className="hidden sm:block">
              <Table>
                <thead>
                  <tr>
                    <th>{t("templates.name")}</th>
                    <th>{t("templates.category")}</th>
                    <th>{t("templates.language")}</th>
                    <th>{t("templates.status")}</th>
                    <th>
                      <span className="sr-only">{t("templates.actions")}</span>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((template) => (
                    <tr key={template.id}>
                      <td>
                        <p className="font-medium">{template.name}</p>
                        <p className="max-w-md truncate text-xs text-mute-600">
                          {template.body}
                        </p>
                      </td>
                      <td>{t(`templates.categories.${template.category}`)}</td>
                      <td>{language(template)}</td>
                      <td>
                        <StatusTag template={template} />
                      </td>
                      <td className="text-right whitespace-nowrap">
                        <RowActions
                          template={template}
                          onEdit={() => setEditing({ template })}
                          onDelete={() => setDeleting(template)}
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>

            <ul className="sm:hidden">
              {rows.map((template) => (
                <li
                  key={template.id}
                  className="border-b border-rule px-4 py-3 last:border-0"
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      {/* `break-all`: a template name is one long word of
                          underscores, and nothing else would wrap it. */}
                      <p className="font-medium break-all">{template.name}</p>
                      <p className="text-xs text-mute-600">
                        {t(`templates.categories.${template.category}`)} ·{" "}
                        {language(template)}
                      </p>
                    </div>
                    <StatusTag template={template} />
                  </div>
                  <p className="mt-1 line-clamp-2 text-xs text-mute-600">
                    {template.body}
                  </p>
                  <div className="mt-2 -ml-1 flex gap-1">
                    <RowActions
                      template={template}
                      onEdit={() => setEditing({ template })}
                      onDelete={() => setDeleting(template)}
                    />
                  </div>
                </li>
              ))}
            </ul>
          </>
        )}
      </Card>

      {editing ? (
        <PlantillaForm
          {...(editing.template ? { template: editing.template } : {})}
          onClose={() => setEditing(null)}
        />
      ) : null}
      {deleting ? (
        <ConfirmDelete template={deleting} onClose={() => setDeleting(null)} />
      ) : null}
    </Page>
  );
}

/** Named per template, so a list of them reads as more than "Editar, Editar". */
function RowActions({
  template,
  onEdit,
  onDelete,
}: {
  template: ManagedTemplate;
  onEdit: () => void;
  onDelete: () => void;
}) {
  const { t } = useTranslation();
  return (
    <>
      <Button
        variant="ghost"
        aria-label={t("templates.editNamed", { name: template.name })}
        onClick={onEdit}
      >
        {t("templates.edit")}
      </Button>
      <Button
        variant="ghost"
        aria-label={t("templates.deleteNamed", { name: template.name })}
        onClick={onDelete}
      >
        {t("templates.delete")}
      </Button>
    </>
  );
}

/** Meta's verdict, and its reason when the verdict is no. */
function StatusTag({ template }: { template: ManagedTemplate }) {
  const { t } = useTranslation();
  const tone =
    template.status === "REJECTED"
      ? "accent"
      : template.status === "APPROVED"
        ? "neutral"
        : "outline";
  return (
    <div className="flex shrink-0 flex-col items-start gap-1">
      <Tag tone={tone}>{t(`templates.statuses.${template.status}`)}</Tag>
      {template.rejected_reason ? (
        <span className="text-xs text-brand-700">{template.rejected_reason}</span>
      ) : null}
    </div>
  );
}

/** Deleting is in Meta too, and Meta holds the name for a month after. */
function ConfirmDelete({
  template,
  onClose,
}: {
  template: ManagedTemplate;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const remove = useDeleteTemplate();

  return (
    <Dialog
      open
      onClose={onClose}
      width={520}
      kicker={template.name}
      title={t("templates.deleteTitle")}
      footer={
        <>
          <Button
            variant="primary"
            disabled={remove.isPending}
            onClick={() => remove.mutate(template.id, { onSuccess: onClose })}
          >
            {remove.isPending ? t("templates.deleting") : t("templates.deleteConfirm")}
          </Button>
          <Button variant="ghost" className="ml-auto" onClick={onClose}>
            {t("common.cancel")}
          </Button>
        </>
      }
    >
      <p className="text-sm">{t("templates.deleteLead")}</p>
      {remove.isError ? (
        <p role="alert" className="mt-3 text-sm text-brand-700">
          {remove.error.message}
        </p>
      ) : null}
    </Dialog>
  );
}
